/**
 * Build Your Flock — visual bundle selector for Holy Duck! Co.
 *
 * A self-contained custom element. All state and listeners live on the
 * element instance, so multiple section instances coexist and Shopify
 * theme-editor section reloads (which replace the DOM node) can't leak
 * listeners. Cart integration mirrors Dawn's product-form.js: one Ajax
 * request to routes.cart_add_url with an items array + rendered sections,
 * then publish(cartUpdate) and cart.renderContents() so Dawn's own cart
 * drawer / notification / icon bubble machinery does the rest.
 */
if (!customElements.get('build-your-flock')) {
  customElements.define(
    'build-your-flock',
    class BuildYourFlock extends HTMLElement {
      connectedCallback() {
        const configEl = this.querySelector('[data-byf-config]');
        if (!configEl) return;
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (error) {
          console.error('[build-your-flock] Invalid config JSON', error);
          return;
        }

        this.strings = this.config.strings || {};
        this.selection = [];
        this.sets = 1;
        this.maxSets = 10;
        this.pending = false;
        this.barDismissed = false;

        this.refs = {
          sizeInputs: Array.from(this.querySelectorAll('[data-byf-size-input]')),
          tabs: Array.from(this.querySelectorAll('[data-byf-tab]')),
          tablist: this.querySelector('[data-byf-tablist]'),
          panel: this.querySelector('[role="tabpanel"]'),
          tiles: Array.from(this.querySelectorAll('[data-byf-tile]')),
          slots: Array.from(this.querySelectorAll('[data-byf-slot]')),
          progress: this.querySelector('[data-byf-progress]'),
          trayMessage: this.querySelector('[data-byf-tray-message]'),
          count: this.querySelector('[data-byf-count]'),
          subtotal: this.querySelector('[data-byf-subtotal]'),
          addButton: this.querySelector('[data-byf-add]'),
          addLabel: this.querySelector('[data-byf-add-label]'),
          spinner: this.querySelector('[data-byf-spinner]'),
          error: this.querySelector('[data-byf-error]'),
          success: this.querySelector('[data-byf-success]'),
          live: this.querySelector('[data-byf-live]'),
          gridEmpty: this.querySelector('[data-byf-grid-empty]'),
          setsCount: this.querySelector('[data-byf-sets-count]'),
          setsMinus: this.querySelector('[data-byf-sets-minus]'),
          setsPlus: this.querySelector('[data-byf-sets-plus]'),
          setsNote: this.querySelector('[data-byf-sets-note]'),
          bar: this.querySelector('[data-byf-bar]'),
          barProgress: this.querySelector('[data-byf-bar-progress]'),
          barSubtotal: this.querySelector('[data-byf-bar-subtotal]'),
          barAdd: this.querySelector('[data-byf-bar-add]'),
          barAddLabel: this.querySelector('[data-byf-bar-add-label]'),
        };

        const checked = this.refs.sizeInputs.find((input) => input.checked) || this.refs.sizeInputs[0];
        this.size = checked ? parseInt(checked.dataset.qty, 10) : this.config.smallQty;
        this.sizeValue = checked ? checked.value : 'small';

        this.onClickBound = this.onClick.bind(this);
        this.onChangeBound = this.onChange.bind(this);
        this.onKeydownBound = this.onKeydown.bind(this);
        this.addEventListener('click', this.onClickBound);
        this.addEventListener('change', this.onChangeBound);
        if (this.refs.tablist) this.refs.tablist.addEventListener('keydown', this.onKeydownBound);

        const firstTab = this.refs.tabs[0];
        this.setActiveTab(firstTab ? firstTab.dataset.byfTab : 'all');

        if (this.refs.bar) {
          this.barObserver = new IntersectionObserver(
            (entries) => {
              this.barVisible = entries.some((entry) => entry.isIntersecting);
              this.updateBarVisibility();
            },
            { rootMargin: '-120px 0px -80px 0px' }
          );
          this.barObserver.observe(this);
        }

        this.render();
      }

      disconnectedCallback() {
        if (this.barObserver) this.barObserver.disconnect();
        this.removeEventListener('click', this.onClickBound);
        this.removeEventListener('change', this.onChangeBound);
        if (this.refs && this.refs.tablist) this.refs.tablist.removeEventListener('keydown', this.onKeydownBound);
      }

      /* ---------------------------------------------------------- events */

      onClick(event) {
        const tab = event.target.closest('[data-byf-tab]');
        if (tab) return this.setActiveTab(tab.dataset.byfTab, true);

        const select = event.target.closest('[data-byf-select]');
        if (select) return this.onTileActivate(select);

        const plus = event.target.closest('[data-byf-plus]');
        if (plus) return this.addOne(this.tileFor(plus).dataset.variantId);

        const minus = event.target.closest('[data-byf-minus]');
        if (minus) return this.removeOne(this.tileFor(minus).dataset.variantId);

        const setsPlus = event.target.closest('[data-byf-sets-plus]');
        if (setsPlus) return this.changeSets(1);

        const setsMinus = event.target.closest('[data-byf-sets-minus]');
        if (setsMinus) return this.changeSets(-1);

        const slotRemove = event.target.closest('[data-byf-slot-remove]');
        if (slotRemove) {
          const index = this.refs.slots.indexOf(slotRemove.closest('[data-byf-slot]'));
          if (index > -1) this.removeAt(index);
          return;
        }

        if (event.target.closest('[data-byf-add]') || event.target.closest('[data-byf-bar-add]')) {
          return this.addToCart();
        }

        if (event.target.closest('[data-byf-bar-dismiss]')) {
          this.barDismissed = true;
          this.updateBarVisibility();
        }
      }

      onChange(event) {
        const input = event.target.closest('[data-byf-size-input]');
        if (input) this.onSizeChange(input);
      }

      onKeydown(event) {
        const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const tabs = this.refs.tabs;
        const currentIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        let nextIndex = currentIndex;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        const next = tabs[nextIndex];
        if (next) {
          this.setActiveTab(next.dataset.byfTab, true);
          next.focus();
        }
      }

      tileFor(el) {
        return el.closest('[data-byf-tile]');
      }

      /* ------------------------------------------------------- selection */

      onTileActivate(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;
        const variantId = button.dataset.variantId;
        const selectedQty = this.countOf(variantId);

        if (selectedQty > 0 && !this.config.allowRepeats) {
          return this.removeOne(variantId);
        }
        this.addOne(variantId);
      }

      addOne(variantId) {
        if (this.selection.length >= this.size) {
          this.setStatus(this.strings.flockFull);
          return;
        }
        const button = this.selectButtonFor(variantId);
        if (!button || button.getAttribute('aria-disabled') === 'true') return;
        const max = parseInt(button.dataset.max, 10) || 0;
        if (max > 0 && this.countOf(variantId) + 1 > max) {
          this.setStatus(this.strings.flockFull ? `Only ${max} available of ${button.dataset.name}.` : '');
          return;
        }
        this.selection.push(variantId);
        this.clearMessages();
        this.setStatus(this.fill(this.strings.added, { name: button.dataset.name }));
        this.render();
        this.bounce(this.tileFor(button));
      }

      removeOne(variantId) {
        const index = this.selection.lastIndexOf(variantId);
        if (index === -1) return;
        this.removeAt(index);
      }

      removeAt(index) {
        const variantId = this.selection[index];
        if (!variantId) return;
        this.selection.splice(index, 1);
        const button = this.selectButtonFor(variantId);
        this.clearMessages();
        this.setStatus(this.fill(this.strings.removed, { name: button ? button.dataset.name : '' }));
        this.render();
      }

      countOf(variantId) {
        return this.selection.filter((id) => id === variantId).length;
      }

      selectButtonFor(variantId) {
        return this.querySelector(`[data-byf-select][data-variant-id="${variantId}"]`);
      }

      onSizeChange(input) {
        const newQty = parseInt(input.dataset.qty, 10);
        if (this.selection.length > newQty) {
          // Never silently drop picks: refuse the switch and explain.
          input.checked = false;
          const current = this.refs.sizeInputs.find((radio) => radio.value === this.sizeValue);
          if (current) current.checked = true;
          const excess = this.selection.length - newQty;
          const message = this.fill(this.strings.shrinkBlocked, {
            count: this.selection.length,
            excess: `${excess} ${excess === 1 ? 'duck' : 'ducks'}`,
          });
          this.showError(message);
          this.setStatus(message);
          return;
        }
        this.size = newQty;
        this.sizeValue = input.value;
        this.clearMessages();
        this.render();
      }

      /* ------------------------------------------------------------- kit */

      /**
       * The flock is sold as ONE product (the kit) so the cart shows a single
       * priced line instead of 4-5 loose caps. The colour picks ride along as a
       * line-item property. Falls back to adding the individual caps only if no
       * kit product is configured.
       */
      kitVariantFor() {
        const list = (this.config.kit && this.config.kit.variants) || [];
        if (!list.length) return null;
        /* Positional, not name-matched. Matching a number in the variant title
           breaks as soon as the pick count collides with a cap count - a 5-set
           bundle would always select the "5 caps per set" variant whichever
           button was pressed. The kit product's variants must therefore be
           ordered to match the size options: small first, full second. */
        return list[this.sizeValue === 'full' ? 1 : 0] || list[0];
      }

      /** "Blue, Green x2, Red" - what the customer actually chose. */
      colorSummary() {
        const counts = new Map();
        this.selection.forEach((variantId) => {
          const button = this.selectButtonFor(variantId);
          const name = button ? button.dataset.name : null;
          if (name) counts.set(name, (counts.get(name) || 0) + 1);
        });
        return Array.from(counts.entries())
          .map(([name, qty]) => (qty > 1 ? `${name} ×${qty}` : name))
          .join(', ');
      }

      /* ------------------------------------------------------------ sets */

      /**
       * Number of identical copies of the chosen flock to add. The colour
       * picks are unchanged - this only multiplies the cart quantities.
       */
      changeSets(delta) {
        const next = Math.min(this.maxSets, Math.max(1, this.sets + delta));
        if (next === this.sets) return;
        this.sets = next;
        this.clearMessages();
        this.render();
      }

      /* ------------------------------------------------------------ tabs */

      setActiveTab(category, focusPanelUpdate = false) {
        this.activeTab = category;
        this.refs.tabs.forEach((tab) => {
          const active = tab.dataset.byfTab === category;
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
          tab.setAttribute('tabindex', active ? '0' : '-1');
          if (active && this.refs.panel) this.refs.panel.setAttribute('aria-labelledby', tab.id);
        });

        let visible = 0;
        this.refs.tiles.forEach((tile) => {
          const show = category === 'all' || tile.dataset.category === category;
          tile.hidden = !show;
          if (show) visible += 1;
        });

        if (this.refs.gridEmpty) {
          this.refs.gridEmpty.hidden = visible > 0;
          this.refs.gridEmpty.textContent = visible > 0 ? '' : this.strings.noDucks;
        }
        if (focusPanelUpdate) this.clearMessages();
      }

      /* ---------------------------------------------------------- render */

      render() {
        const count = this.selection.length;
        const complete = count === this.size;

        // Slots
        this.refs.slots.forEach((slot, index) => {
          const inUse = index < this.size;
          slot.hidden = !inUse;
          if (!inUse) return;

          const variantId = this.selection[index];
          const emptyEl = slot.querySelector('.byf-slot__empty');
          const filledEl = slot.querySelector('.byf-slot__filled');

          if (variantId) {
            const button = this.selectButtonFor(variantId);
            const name = button ? button.dataset.name : '';
            const image = button ? button.dataset.image : '';
            const img = slot.querySelector('[data-byf-slot-img]');
            if (img) {
              if (image) {
                img.src = image;
                img.hidden = false;
              } else {
                img.removeAttribute('src');
                img.hidden = true;
              }
              img.alt = '';
            }
            const nameEl = slot.querySelector('[data-byf-slot-name]');
            if (nameEl) nameEl.textContent = name;
            const removeLabel = slot.querySelector('[data-byf-slot-remove-label]');
            if (removeLabel) removeLabel.textContent = `Remove ${name} from slot ${index + 1}`;
            emptyEl.hidden = true;
            filledEl.hidden = false;
            slot.classList.add('byf-slot--filled');
          } else {
            emptyEl.hidden = false;
            filledEl.hidden = true;
            slot.classList.remove('byf-slot--filled');
          }
        });

        // Progress + tray message
        const progressText = this.fill(this.strings.progress, { count, total: this.size });
        if (this.refs.progress) this.refs.progress.textContent = progressText;
        if (this.refs.barProgress) this.refs.barProgress.textContent = `${count}/${this.size}`;
        if (this.refs.trayMessage) {
          this.refs.trayMessage.textContent =
            count === 0 ? this.strings.empty : complete ? this.strings.complete : progressText;
          this.refs.trayMessage.classList.toggle('byf-tray__message--complete', complete);
        }

        // Subtotal
        const kitVariant = this.kitVariantFor();
        const perSet = kitVariant
          ? parseInt(kitVariant.price, 10) || 0
          : this.selection.reduce((sum, variantId) => {
              const button = this.selectButtonFor(variantId);
              return sum + (button ? parseInt(button.dataset.price, 10) || 0 : 0);
            }, 0);
        const subtotal = perSet * this.sets;
        if (this.refs.count) this.refs.count.textContent = String(count * this.sets);
        if (this.refs.setsCount) this.refs.setsCount.textContent = String(this.sets);
        if (this.refs.setsMinus) this.refs.setsMinus.disabled = this.sets <= 1;
        if (this.refs.setsPlus) this.refs.setsPlus.disabled = this.sets >= this.maxSets;
        if (this.refs.setsNote) {
          this.refs.setsNote.hidden = this.sets < 2;
          this.refs.setsNote.textContent =
            this.sets < 2 ? '' : `${this.sets} identical sets of ${this.size} ducks`;
        }
        const money = this.formatMoney(subtotal);
        if (this.refs.subtotal) this.refs.subtotal.textContent = money;
        if (this.refs.barSubtotal) this.refs.barSubtotal.textContent = money;

        // Buttons
        const label = complete ? this.strings.buttonComplete : this.strings.buttonIncomplete;
        [this.refs.addButton, this.refs.barAdd].forEach((button) => {
          if (!button) return;
          button.disabled = !complete || this.pending;
        });
        if (this.refs.addLabel && !this.pending) this.refs.addLabel.textContent = label;
        if (this.refs.barAddLabel && !this.pending) this.refs.barAddLabel.textContent = label;

        // Tiles
        this.refs.tiles.forEach((tile) => {
          const button = tile.querySelector('[data-byf-select]');
          if (!button) return;
          const variantId = button.dataset.variantId;
          const qty = this.countOf(variantId);
          const selected = qty > 0;
          tile.classList.toggle('byf-tile--selected', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');

          const srState = button.querySelector('[data-byf-sr-state]');
          if (srState) srState.textContent = selected ? `. ${this.fill(this.strings.selectedState, { qty })}` : '';

          const bubble = tile.querySelector('[data-byf-qty-bubble]');
          if (bubble) {
            bubble.hidden = qty < 2;
            bubble.textContent = qty >= 2 ? `×${qty}` : '';
          }

          const controls = tile.querySelector('[data-byf-qty-controls]');
          if (controls) {
            controls.hidden = !(selected && this.config.allowRepeats);
            const countEl = controls.querySelector('[data-byf-qty-count]');
            if (countEl) countEl.textContent = String(qty);
            const plus = controls.querySelector('[data-byf-plus]');
            if (plus) {
              const max = parseInt(button.dataset.max, 10) || 0;
              plus.disabled = this.selection.length >= this.size || (max > 0 && qty >= max);
            }
          }
        });

        this.updateBarVisibility();
      }

      updateBarVisibility() {
        if (!this.refs.bar) return;
        this.refs.bar.hidden = !(this.config.stickyMobile && this.barVisible && !this.barDismissed);
      }

      bounce(tile) {
        if (!tile) return;
        tile.classList.remove('byf-tile--bounce');
        // Force a reflow so the animation can replay on repeat selections.
        void tile.offsetWidth;
        tile.classList.add('byf-tile--bounce');
      }

      /* ------------------------------------------------------------- cart */

      async addToCart() {
        if (this.pending || this.selection.length !== this.size) return;
        this.pending = true;
        this.clearMessages();

        const buttons = [this.refs.addButton, this.refs.barAdd].filter(Boolean);
        buttons.forEach((button) => {
          button.disabled = true;
          button.classList.add('loading');
        });
        if (this.refs.spinner) this.refs.spinner.hidden = false;
        if (this.refs.addLabel) this.refs.addLabel.textContent = this.strings.adding;
        this.setStatus(this.strings.adding);

        const sets = this.sets;
        const kitVariant = this.kitVariantFor();
        let items;

        if (kitVariant) {
          const properties = { Colors: this.colorSummary() };
          if (this.config.addLineProperty) properties.Bundle = 'Build Your Flock';
          items = [{ id: parseInt(kitVariant.id, 10), quantity: sets, properties }];
        } else {
          // No kit product configured - fall back to adding the caps individually.
          const quantities = new Map();
          this.selection.forEach((variantId) => {
            quantities.set(variantId, (quantities.get(variantId) || 0) + 1);
          });
          items = Array.from(quantities.entries()).map(([id, quantity]) => {
            const item = { id: parseInt(id, 10), quantity: quantity * sets };
            if (this.config.addLineProperty) item.properties = { Bundle: 'Build Your Flock' };
            return item;
          });
        }

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' } };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        try {
          const response = await fetch(addUrl, config);
          const data = await response.json();

          if (data.status) {
            // Shopify rejected the add (e.g. an item sold out after page load).
            const message = data.description || data.message || this.strings.cartError;
            this.showError(message);
            this.setStatus(message);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, { source: 'build-your-flock', errors: message, message });
            }
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'build-your-flock', cartData: data });
          }

          if (cart && typeof cart.renderContents === 'function') {
            /* Dawn puts `is-empty` on <cart-drawer> itself, and renderContents only
               swaps that element's innerHTML - so the class survives and the CSS
               keeps the line items hidden. Dawn's own product-form.js clears it
               before rendering; do the same or the drawer looks broken on the
               first add to an empty cart. */
            cart.classList.remove('is-empty');
            const drawerInner = cart.querySelector('.drawer__inner');
            if (drawerInner) drawerInner.classList.remove('is-empty');

            cart.renderContents(data);

            /* renderContents can rebuild .drawer__inner from the fetched section,
               which may reintroduce the class. Clear it again on the next frame. */
            requestAnimationFrame(() => {
              cart.classList.remove('is-empty');
              const inner = cart.querySelector('.drawer__inner');
              if (inner) inner.classList.remove('is-empty');
            });
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          if (this.refs.success) {
            this.refs.success.textContent = this.strings.success;
            this.refs.success.hidden = false;
          }
          this.setStatus(this.strings.success);
          if (this.config.resetAfterAdd) this.selection = [];
        } catch (error) {
          console.error('[build-your-flock] Add to cart failed', error);
          this.showError(this.strings.cartError);
          this.setStatus(this.strings.cartError);
        } finally {
          this.pending = false;
          buttons.forEach((button) => button.classList.remove('loading'));
          if (this.refs.spinner) this.refs.spinner.hidden = true;
          this.render();
        }
      }

      /* -------------------------------------------------------- messaging */

      clearMessages() {
        if (this.refs.error) {
          this.refs.error.hidden = true;
          this.refs.error.textContent = '';
        }
        if (this.refs.success) {
          this.refs.success.hidden = true;
          this.refs.success.textContent = '';
        }
      }

      showError(message) {
        if (!this.refs.error) return;
        this.refs.error.textContent = message;
        this.refs.error.hidden = false;
      }

      setStatus(message) {
        if (!this.refs.live || !message) return;
        // Clear first so repeated identical messages are re-announced. Use a
        // timeout (not requestAnimationFrame, which is throttled in background
        // tabs) so the announcement fires reliably for screen-reader users.
        this.refs.live.textContent = '';
        clearTimeout(this._liveTimer);
        this._liveTimer = setTimeout(() => {
          this.refs.live.textContent = message;
        }, 60);
      }

      fill(template, values) {
        if (!template) return '';
        return Object.entries(values).reduce(
          (result, [key, value]) => result.split(`[${key}]`).join(String(value)),
          template
        );
      }

      formatMoney(cents) {
        const format = (this.config.moneyFormat || '').replace(/<[^>]*>/g, '');
        const fixed = (cents / 100).toFixed(2);
        const [whole, decimals] = fixed.split('.');
        const group = (num, separator) => num.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
        const replacements = {
          amount: `${group(whole, ',')}.${decimals}`,
          amount_no_decimals: group(whole, ','),
          amount_with_comma_separator: `${group(whole, '.')},${decimals}`,
          amount_no_decimals_with_comma_separator: group(whole, '.'),
          amount_with_apostrophe_separator: `${group(whole, "'")}.${decimals}`,
        };
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (match && replacements[match[1]] != null) {
          return format.replace(/\{\{\s*\w+\s*\}\}/, replacements[match[1]]);
        }
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: this.config.currency || 'USD',
          }).format(cents / 100);
        } catch (error) {
          return `$${fixed}`;
        }
      }
    }
  );
}
